import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { companiesService } from 'src/app/shared/vive-api/companies.service';
import {
  SsoConnectionDto,
  SsoConnectionDtoProtocolEnum,
  SsoConnectionDtoScopeEnum,
  SsoConnectionDtoStatusEnum,
  SsoConnectionsRoleMappingRequestDtoUserAccountRoleEnum
} from 'src/app/shared/vive-api/vive-api-models';
import { ViveApiError } from 'src/app/shared/vive-api/vive-api.error';

// Shared by the SSO connection list, the connection page, its cards and the
// add dialog.

export type SsoRole = SsoConnectionsRoleMappingRequestDtoUserAccountRoleEnum;

// A role mapping comes back with UserAccountRoleId only, and UserAccountRoles ids
// are the same in every environment. Only the roles a connection may grant are
// listed: the API refuses a mapping to any other.
export const SSO_ROLE_BY_ID: ReadonlyMap<number, SsoRole> = new Map<
  number,
  SsoRole
>([
  [1, 'CLIENT_ADMIN'],
  [2, 'CLIENT_VENDOR_AND_SITES_MANAGER'],
  [12, 'ADMIN_ADMIN'],
  [25, 'ADMIN_PRIME_TEAM'],
  [26, 'ADMIN_EMAILS_TEAM'],
  [27, 'ADMIN_INSURANCE_TEAM'],
  [28, 'ADMIN_FRAUD_TEAM'],
  [29, 'ADMIN_CUSTOMER_SERVICE_TEAM'],
  [30, 'ADMIN_PROJECT_MANAGEMENT_TEAM']
]);

// In dropdown order. Must match the API's allow-list for each scope, which
// refuses everything else.
export const SSO_ROLES_FOR_SCOPE: Readonly<
  Record<SsoConnectionDtoScopeEnum, readonly SsoRole[]>
> = {
  staff: [
    'ADMIN_ADMIN',
    'ADMIN_CUSTOMER_SERVICE_TEAM',
    'ADMIN_PRIME_TEAM',
    'ADMIN_PROJECT_MANAGEMENT_TEAM',
    'ADMIN_INSURANCE_TEAM',
    'ADMIN_FRAUD_TEAM',
    'ADMIN_EMAILS_TEAM'
  ],
  client: ['CLIENT_ADMIN', 'CLIENT_VENDOR_AND_SITES_MANAGER']
};

// Not the roles' displayName: CLIENT_ADMIN and ADMIN_ADMIN are both "Admin" there.
export const SSO_ROLE_LABELS: Readonly<Partial<Record<SsoRole, string>>> = {
  ADMIN_ADMIN: 'Admin (full access)',
  ADMIN_CUSTOMER_SERVICE_TEAM: 'Customer service',
  ADMIN_PRIME_TEAM: 'PRIME',
  ADMIN_PROJECT_MANAGEMENT_TEAM: 'Project management',
  ADMIN_INSURANCE_TEAM: 'Insurance',
  ADMIN_FRAUD_TEAM: 'Fraud',
  ADMIN_EMAILS_TEAM: 'Emails',
  CLIENT_ADMIN: 'Client admin',
  CLIENT_VENDOR_AND_SITES_MANAGER: 'Vendor and sites manager'
};

export const SSO_PROTOCOL_LABELS: Readonly<
  Record<SsoConnectionDtoProtocolEnum, string>
> = {
  oidc: 'OIDC',
  saml: 'SAML'
};

export const SSO_STATUS_CHIPS: Readonly<
  Record<SsoConnectionDtoStatusEnum, { label: string; className: string }>
> = {
  draft: { label: 'Draft', className: 'chip-neutral' },
  active: { label: 'Active', className: 'chip-success' },
  disabled: { label: 'Disabled', className: 'chip-warning' }
};

export const SSO_SAVE_FAILED = "We couldn't save this. Try again.";

// Activate checks the identity provider fields only. With no domain nobody is sent
// to the connection, and with no mapping nobody it receives can sign in, so the
// screen asks for both first. Reads the SAVED connection, not a card's edits.
export const hasDomainAndMapping = (connection: SsoConnectionDto): boolean =>
  (connection.Domains ?? []).length > 0 &&
  (connection.RoleMappings ?? []).length > 0;

const STATUSES_WITH_ADMIN_MESSAGES = new Set([400, 409]);

// The client throws a wrapper message ("vive-api fetch PUT error [409]") and keeps
// Nest's body, `{ statusCode, message: string | string[] }`, on `cause`. Only a 400
// or a 409 from these routes carries a message written for the admin.
export const ssoApiErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  if (
    !(error instanceof ViveApiError) ||
    !STATUSES_WITH_ADMIN_MESSAGES.has(error.httpStatusCode ?? 0)
  ) {
    return fallback;
  }

  const { cause } = error;
  if (typeof cause !== 'object' || cause === null || !('message' in cause)) {
    return fallback;
  }

  const { message } = cause;
  if (typeof message === 'string' && message !== '') {
    return message;
  }

  if (
    Array.isArray(message) &&
    message.length > 0 &&
    message.every((line): line is string => typeof line === 'string')
  ) {
    return message.join('; ');
  }

  return fallback;
};

// The menu hides the screen from everyone but ADMIN_ADMIN, and no route guard
// stops a direct URL, so a load can answer 403.
export const ssoLoadErrorMessage = (error: unknown): string =>
  error instanceof ViveApiError && error.httpStatusCode === 403
    ? 'Only VIVE admins with full access can manage SSO connections.'
    : "We couldn't load SSO connections. Try again.";

// A client whose lookup fails, or who has no legal name, is left out; the label
// falls back to the id.
export const loadClientNames = async (
  companyIds: readonly number[]
): Promise<Map<number, string>> => {
  const ids = [...new Set(companyIds)];
  const results = await Promise.allSettled(
    ids.map((companyId) =>
      companiesService.findOneById({ pathParams: { companyId } })
    )
  );
  const names = new Map<number, string>();

  results.forEach((result, index) => {
    if (result.status === 'fulfilled' && result.value.legalName) {
      names.set(ids[index], result.value.legalName);
    }
  });

  return names;
};

// A staff connection has no CompanyId and a client connection always has one: the
// API's model refuses any other pairing.
export const ssoConnectionForLabel = (
  connection: SsoConnectionDto,
  clientNames: ReadonlyMap<number, string>
): string => {
  const { CompanyId } = connection;

  return CompanyId === null
    ? 'VIVE staff'
    : (clientNames.get(CompanyId) ?? `Client ${CompanyId}`);
};

export const confirmSsoDisable = async (
  modalService: NgbModal,
  name: string
): Promise<boolean> => {
  const { componentInstance, result } = modalService.open(
    ConfirmModalComponent,
    { backdrop: 'static', centered: true, size: 'md' }
  );

  Object.assign(componentInstance, {
    title: `Disable ${name}?`,
    displayText:
      'Everyone signed in through this connection is signed out now, and nobody can use it to sign in until it is activated again.',
    consequenceText:
      'Its domains stay with this connection, so no other connection can take them until you remove them here.',
    continueButtonText: 'Disable connection',
    confirmTone: 'danger'
  });

  // Escape dismisses the dialog, which rejects the result.
  try {
    return (await result) === true;
  } catch {
    return false;
  }
};
